"use client";

import { c as _c } from "react/compiler-runtime";
import PrimaryButton from "@/components/utils/button/PrimaryButton";
import SecondaryButton from "@/components/utils/button/SecondaryButton";
import MemesArtSubmissionFile from "@/components/waves/memes/MemesArtSubmissionFile";
import MemesArtSubmissionTraits from "@/components/waves/memes/MemesArtSubmissionTraits";
import React, { useCallback, useMemo } from "react";
import type { InteractiveMediaMimeType, InteractiveMediaProvider } from "../constants/media";
import ArtworkDetails from "../details/ArtworkDetails";
import type { TraitsData } from "../types/TraitsData";
import type { SubmissionPhase } from "../ui/SubmissionProgress";
import SubmissionProgress from "../ui/SubmissionProgress";
import { useTraitsValidation } from "../validation";

/**
 * Required fields for submission
 * All fields are required for submission
 */

/**
 * Fields that are read-only and should be skipped in validation
 * These are pre-populated by the system
 */
const READ_ONLY_FIELDS = ["seizeArtistProfile"] as const;

/**
 * Get tooltip text for submit button based on current state
 * Helps users understand why the button might be disabled
 */
function getSubmitButtonTooltip(isDisabled: boolean, isFormComplete: boolean, artworkUploaded: boolean, traits: TraitsData): string {
  if (!isDisabled) return "";
  if (!artworkUploaded) {
    return "Please upload artwork";
  }
  if (!isFormComplete) {
    // Check each field and return specific message for the first empty one
    // This helps the user understand what's missing
    for (const field of Object.keys(traits) as Array<keyof TraitsData>) {
      // Skip read-only fields
      if (READ_ONLY_FIELDS.includes(field as any)) continue;
      const value = traits[field];
      const fieldName = field.replace(/([A-Z])/g, " $1").toLowerCase();

      // String fields (including dropdowns)
      if (typeof value === "string") {
        if (!value.trim()) {
          if (field === "memeName" || field === "palette") {
            return `Please select a ${fieldName}`;
          } else {
            return `Please enter ${fieldName}`;
          }
        }
      }
      // Number fields
      else if (typeof value === "number") {
        if (value === null || value === undefined || isNaN(value)) {
          return `Please enter ${fieldName}`;
        }
      }
      // Boolean fields need explicit value
      else if (typeof value === "boolean") {
        if (value === null || value === undefined) {
          return `Please set ${fieldName}`;
        }
      }
      // Handle potential null/undefined
      else if (value === null || value === undefined) {
        return `Please complete ${fieldName}`;
      }
    }
    return "All fields are required";
  }
  return "Please fix validation errors before submitting";
}

/**
 * Checks if form meets basic submission requirements
 * ALL fields are required except read-only ones
 */
function checkFormCompleteness(traits: TraitsData, artworkUploaded: boolean): boolean {
  // Must have artwork
  if (!artworkUploaded) return false;

  // Check every field in TraitsData
  for (const field of Object.keys(traits) as Array<keyof TraitsData>) {
    // Skip read-only fields that are pre-populated
    if (READ_ONLY_FIELDS.includes(field as any)) continue;
    const value = traits[field];

    // Handle string fields - must not be empty
    if (typeof value === "string") {
      if (!value.trim()) return false;
    }
    // Handle number fields - must be a defined number and not zero
    else if (typeof value === "number") {
      if (value === null || value === undefined || isNaN(value) || value === 0) return false;
    }
    // Handle boolean fields - must be explicitly true or false
    else if (typeof value === "boolean") {
      if (value === null || value === undefined) return false;
    }
    // Handle potential null/undefined values
    else if (value === null || value === undefined) {
      return false;
    }
  }
  return true;
}
interface ArtworkStepProps {
  readonly traits: TraitsData;
  readonly artworkUploaded: boolean;
  readonly artworkUrl: string;
  readonly setArtworkUploaded: (uploaded: boolean) => void;
  readonly handleFileSelect: (file: File) => void;
  readonly mediaSource: "upload" | "url";
  readonly setMediaSource: (mode: "upload" | "url") => void;
  readonly externalHash: string;
  readonly externalProvider: InteractiveMediaProvider;
  readonly externalConstructedUrl: string;
  readonly externalPreviewUrl: string;
  readonly externalMimeType: InteractiveMediaMimeType;
  readonly externalError: string | null;
  readonly externalValidationStatus: "idle" | "pending" | "valid" | "invalid";
  readonly isExternalMediaValid: boolean;
  readonly onExternalHashChange: (value: string) => void;
  readonly onExternalProviderChange: (value: InteractiveMediaProvider) => void;
  readonly onExternalMimeTypeChange: (value: InteractiveMediaMimeType) => void;
  readonly onClearExternalMedia: () => void;
  readonly onSubmit: () => void;
  readonly onCancel?: (() => void) | undefined; // Added cancel handler prop
  readonly updateTraitField: <K extends keyof TraitsData>(field: K, value: TraitsData[K]) => void;
  readonly setTraits: (traits: Partial<TraitsData>) => void;
  readonly isSubmitting?: boolean | undefined;
  readonly submissionPhase?: SubmissionPhase | undefined;
  readonly initialTraits?: TraitsData | undefined;
  // Additional props for SubmissionProgress
  readonly uploadProgress?: number | undefined;
  readonly fileInfo?: {
    name: string;
    size: number;
  } | null | undefined;
  readonly submissionError?: string | undefined;
}

/**
 * ArtworkStep - Component for the artwork submission step
 *
 * This component directly includes all the needed components for
 * the artwork submission process in a clear, sequential layout.
 * The submit button is fixed at the bottom of the page and changes
 * appearance based on the current submission phase.
 */
const ArtworkStep: React.FC<ArtworkStepProps> = t0 => {
  const $ = _c(91);
  const {
    traits,
    artworkUploaded,
    artworkUrl,
    setArtworkUploaded,
    handleFileSelect,
    mediaSource,
    setMediaSource,
    externalHash,
    externalProvider,
    externalConstructedUrl,
    externalPreviewUrl,
    externalMimeType,
    externalError,
    externalValidationStatus,
    isExternalMediaValid,
    onExternalHashChange,
    onExternalProviderChange,
    onExternalMimeTypeChange,
    onClearExternalMedia,
    onSubmit,
    onCancel,
    updateTraitField,
    setTraits,
    isSubmitting: t1,
    submissionPhase: t2,
    initialTraits,
    uploadProgress: t3,
    fileInfo: t4,
    submissionError
  } = t0;
  const isSubmitting = t1 === undefined ? false : t1;
  const submissionPhase = t2 === undefined ? "idle" : t2;
  const uploadProgress = t3 === undefined ? 0 : t3;
  const fileInfo = t4 === undefined ? null : t4;
  const validation = useTraitsValidation(traits, initialTraits ?? traits);
  let t5;
  if ($[0] !== artworkUploaded || $[1] !== traits) {
    t5 = checkFormCompleteness(traits, artworkUploaded);
    $[0] = artworkUploaded;
    $[1] = traits;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const isFormComplete = t5;
  let t6;
  if ($[3] !== updateTraitField) {
    t6 = title => {
      updateTraitField("title", title);
    };
    $[3] = updateTraitField;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const handleTitleChange = t6;
  let t7;
  if ($[5] !== updateTraitField) {
    t7 = description => {
      updateTraitField("description", description);
    };
    $[5] = updateTraitField;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const handleDescriptionChange = t7;
  let t8;
  if ($[7] !== validation) {
    t8 = field => {
      validation.markFieldTouched(field);
    };
    $[7] = validation;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const handleFieldBlur = t8;
  let t9;
  if ($[9] !== onSubmit || $[10] !== validation) {
    t9 = () => {
      const validationResult = validation.validateAll();
      if (validationResult.isValid) {
        onSubmit();
      } else {
        validation.focusFirstInvalidField();
      }
    };
    $[9] = onSubmit;
    $[10] = validation;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const handleSubmit = t9;
  let t10;
  bb0: {
    if (!isFormComplete) {
      t10 = true;
      break bb0;
    }
    if (isSubmitting) {
      t10 = true;
      break bb0;
    }
    if (submissionPhase === "success") {
      t10 = true;
      break bb0;
    }
    if (validation.submitAttempted && !validation.isValid) {
      t10 = true;
      break bb0;
    }
    t10 = false;
  }
  const isSubmitDisabled = t10;
  let t11;
  if ($[12] !== submissionPhase) {
    t11 = () => {
      switch (submissionPhase) {
        case "uploading":
          {
            return "Uploading...";
          }
        case "processing":
          {
            return "Processing...";
          }
        case "success":
          {
            return "\u2713 Submission Complete";
          }
        case "error":
          {
            return "Try Again";
          }
        default:
          {
            return "Continue";
          }
      }
    };
    $[12] = submissionPhase;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  const getButtonText = t11;
  let t12;
  if ($[14] !== submissionPhase) {
    t12 = () => {
      switch (submissionPhase) {
        case "success":
          {
            return "tw-bg-green-600 hover:tw-bg-green-700";
          }
        case "error":
          {
            return "tw-bg-red-600 hover:tw-bg-red-700";
          }
        default:
          {
            return "";
          }
      }
    };
    $[14] = submissionPhase;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  const getButtonClass = t12;
  let t13;
  if ($[16] !== artworkUploaded || $[17] !== artworkUrl || $[18] !== externalConstructedUrl || $[19] !== externalError || $[20] !== externalHash || $[21] !== externalMimeType || $[22] !== externalPreviewUrl || $[23] !== externalProvider || $[24] !== externalValidationStatus || $[25] !== handleFileSelect || $[26] !== isExternalMediaValid || $[27] !== mediaSource || $[28] !== onClearExternalMedia || $[29] !== onExternalHashChange || $[30] !== onExternalMimeTypeChange || $[31] !== onExternalProviderChange || $[32] !== setArtworkUploaded || $[33] !== setMediaSource) {
    t13 = () => <MemesArtSubmissionFile artworkUploaded={artworkUploaded} artworkUrl={artworkUrl} setArtworkUploaded={setArtworkUploaded} handleFileSelect={handleFileSelect} mediaSource={mediaSource} setMediaSource={setMediaSource} externalHash={externalHash} externalProvider={externalProvider} externalConstructedUrl={externalConstructedUrl} externalPreviewUrl={externalPreviewUrl} externalMimeType={externalMimeType} externalError={externalError} externalValidationStatus={externalValidationStatus} isExternalMediaValid={isExternalMediaValid} onExternalHashChange={onExternalHashChange} onExternalProviderChange={onExternalProviderChange} onExternalMimeTypeChange={onExternalMimeTypeChange} onClearExternalMedia={onClearExternalMedia} />;
    $[16] = artworkUploaded;
    $[17] = artworkUrl;
    $[18] = externalConstructedUrl;
    $[19] = externalError;
    $[20] = externalHash;
    $[21] = externalMimeType;
    $[22] = externalPreviewUrl;
    $[23] = externalProvider;
    $[24] = externalValidationStatus;
    $[25] = handleFileSelect;
    $[26] = isExternalMediaValid;
    $[27] = mediaSource;
    $[28] = onClearExternalMedia;
    $[29] = onExternalHashChange;
    $[30] = onExternalMimeTypeChange;
    $[31] = onExternalProviderChange;
    $[32] = setArtworkUploaded;
    $[33] = setMediaSource;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  const renderMediaSubmissionPanel = t13;
  let t14;
  if ($[35] !== handleDescriptionChange || $[36] !== handleFieldBlur || $[37] !== handleTitleChange || $[38] !== traits.description || $[39] !== traits.title || $[40] !== validation.errors.description || $[41] !== validation.errors.title) {
    t14 = () => <ArtworkDetails title={traits.title} description={traits.description} onTitleChange={handleTitleChange} onDescriptionChange={handleDescriptionChange} titleError={validation.errors.title} descriptionError={validation.errors.description} onTitleBlur={() => handleFieldBlur("title")} onDescriptionBlur={() => handleFieldBlur("description")} showRequiredMarkers={true} size="sm" />;
    $[35] = handleDescriptionChange;
    $[36] = handleFieldBlur;
    $[37] = handleTitleChange;
    $[38] = traits.description;
    $[39] = traits.title;
    $[40] = validation.errors.description;
    $[41] = validation.errors.title;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  const renderArtworkDetailsPanel = t14;
  let t15;
  if ($[43] !== handleFieldBlur || $[44] !== setTraits || $[45] !== traits || $[46] !== validation.errors) {
    t15 = () => <MemesArtSubmissionTraits traits={traits} setTraits={setTraits} validationErrors={validation.errors} onFieldBlur={handleFieldBlur} showRequiredMarkers={true} size="sm" />;
    $[43] = handleFieldBlur;
    $[44] = setTraits;
    $[45] = traits;
    $[46] = validation.errors;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  const renderArtworkTraitsPanel = t15;
  let t16;
  if ($[48] !== renderMediaSubmissionPanel) {
    t16 = <div className="tw-flex tw-w-full tw-flex-col tw-px-4 tw-pt-4 md:tw-pl-8 lg:tw-min-h-0 lg:tw-w-1/2 lg:tw-pr-4">{renderMediaSubmissionPanel()}</div>;
    $[48] = renderMediaSubmissionPanel;
    $[49] = t16;
  } else {
    t16 = $[49];
  }
  let t17;
  if ($[50] !== renderArtworkDetailsPanel) {
    t17 = renderArtworkDetailsPanel();
    $[50] = renderArtworkDetailsPanel;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  let t18;
  if ($[52] !== renderArtworkTraitsPanel) {
    t18 = renderArtworkTraitsPanel();
    $[52] = renderArtworkTraitsPanel;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  let t19;
  if ($[54] !== t17 || $[55] !== t18) {
    t19 = <div className="tw-w-full tw-px-4 tw-pb-6 tw-pt-6 desktop-hover:hover:tw-scrollbar-thumb-iron-300 md:tw-pl-6 md:tw-pr-8 lg:tw-min-h-0 lg:tw-w-1/2 lg:tw-overflow-y-auto lg:tw-scrollbar-thin lg:tw-scrollbar-track-iron-800 lg:tw-scrollbar-thumb-iron-500"><div className="tw-flex tw-flex-col tw-gap-y-6">{t17}{t18}</div></div>;
    $[54] = t17;
    $[55] = t18;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  let t20;
  if ($[57] !== t16 || $[58] !== t19) {
    t20 = <div data-testid="artwork-step-content" className="tw-min-h-0 tw-w-full tw-flex-1 tw-overflow-y-auto tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 desktop-hover:hover:tw-scrollbar-thumb-iron-300 lg:tw-overflow-hidden"><div className="tw-flex tw-min-h-full tw-w-full tw-flex-col lg:tw-h-full lg:tw-min-h-0 lg:tw-flex-row">{t16}{t19}</div></div>;
    $[57] = t16;
    $[58] = t19;
    $[59] = t20;
  } else {
    t20 = $[59];
  }
  let t21;
  if ($[60] !== fileInfo || $[61] !== submissionError || $[62] !== submissionPhase || $[63] !== uploadProgress) {
    t21 = submissionPhase !== "idle" && <div className="tw-mb-4"><SubmissionProgress phase={submissionPhase} progress={uploadProgress} fileInfo={fileInfo} error={submissionError} /></div>;
    $[60] = fileInfo;
    $[61] = submissionError;
    $[62] = submissionPhase;
    $[63] = uploadProgress;
    $[64] = t21;
  } else {
    t21 = $[64];
  }
  let t22;
  if ($[65] !== onCancel || $[66] !== submissionPhase) {
    t22 = onCancel && submissionPhase !== "success" ? <SecondaryButton onClicked={onCancel} disabled={submissionPhase === "uploading" || submissionPhase === "processing"}>Cancel</SecondaryButton> : <div />;
    $[65] = onCancel;
    $[66] = submissionPhase;
    $[67] = t22;
  } else {
    t22 = $[67];
  }
  const t23 = `tw-transition-all tw-duration-300 ${getButtonClass()}`;
  const t24 = isSubmitting && submissionPhase !== "success" && submissionPhase !== "error";
  let t25;
  if ($[68] !== artworkUploaded || $[69] !== isFormComplete || $[70] !== isSubmitDisabled || $[71] !== traits) {
    t25 = getSubmitButtonTooltip(isSubmitDisabled, isFormComplete, artworkUploaded, traits);
    $[68] = artworkUploaded;
    $[69] = isFormComplete;
    $[70] = isSubmitDisabled;
    $[71] = traits;
    $[72] = t25;
  } else {
    t25 = $[72];
  }
  const t26 = getButtonText();
  let t27;
  if ($[73] !== handleSubmit || $[74] !== isSubmitDisabled || $[75] !== t24 || $[76] !== t25 || $[77] !== t26) {
    t27 = <PrimaryButton onClicked={handleSubmit} loading={t24} disabled={isSubmitDisabled} title={t25}>{t26}</PrimaryButton>;
    $[73] = handleSubmit;
    $[74] = isSubmitDisabled;
    $[75] = t24;
    $[76] = t25;
    $[77] = t26;
    $[78] = t27;
  } else {
    t27 = $[78];
  }
  let t28;
  if ($[79] !== t23 || $[80] !== t27) {
    t28 = <div className={t23}>{t27}</div>;
    $[79] = t23;
    $[80] = t27;
    $[81] = t28;
  } else {
    t28 = $[81];
  }
  let t29;
  if ($[82] !== t22 || $[83] !== t28) {
    t29 = <div className="tw-flex tw-items-center tw-justify-end tw-gap-x-3">{t22}{t28}</div>;
    $[82] = t22;
    $[83] = t28;
    $[84] = t29;
  } else {
    t29 = $[84];
  }
  let t30;
  if ($[85] !== t21 || $[86] !== t29) {
    t30 = <div className="tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-iron-800 tw-bg-iron-950 tw-py-3"><div className="tw-px-4 md:tw-px-8">{t21}{t29}</div></div>;
    $[85] = t21;
    $[86] = t29;
    $[87] = t30;
  } else {
    t30 = $[87];
  }
  let t31;
  if ($[88] !== t20 || $[89] !== t30) {
    t31 = <div className="tw-flex tw-h-full tw-flex-col">{t20}{t30}</div>;
    $[88] = t20;
    $[89] = t30;
    $[90] = t31;
  } else {
    t31 = $[90];
  }
  return t31;
};

// Use React.memo to prevent unnecessary rerenders
export default React.memo(ArtworkStep);